import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { db } from '../services/firebase'
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore'
import { motion } from 'framer-motion'
import SearchBar from '../components/common/SearchBar'
import CenterPitchSlots from '../components/common/CenterPitchSlots'
import { MagnifyingGlassIcon, UserGroupIcon, BuildingOfficeIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { format } from 'date-fns'
import { it, enUS } from 'date-fns/locale'

const Search = () => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'it' ? it : enUS
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '')
  const [matches, setMatches] = useState([])
  const [users, setUsers] = useState([])
  const [centers, setCenters] = useState([])
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('all')

  useEffect(() => {
    if (searchTerm) {
      performSearch()
    }
  }, [searchTerm])

  useEffect(() => {
    const nextSearchTerm = searchParams.get('q') || ''
    setSearchTerm(nextSearchTerm)
    setActiveTab('all')
  }, [searchParams])

  const performSearch = async () => {
    const normalizedTerm = searchTerm.trim().toLowerCase()
    if (!normalizedTerm) {
      setMatches([])
      setUsers([])
      setCenters([])
      return
    }

    setLoading(true)
    try {
      const [matchesSnapshot, usersSnapshot, centersSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'matches'), orderBy('date', 'asc'), limit(100))),
        getDocs(query(collection(db, 'users'), limit(100))),
        getDocs(query(collection(db, 'sportsCenters'), limit(100)))
      ])

      const includesTerm = (value) =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedTerm)

      const matchesData = matchesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(match => {
          const searchable = [
            match.title,
            match.sport,
            match.matchType,
            match.location,
            match.description,
            match.organizerName
          ]
          return searchable.some(includesTerm)
        })
      setMatches(matchesData)

      const usersData = usersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => {
          const searchable = [
            user.firstName,
            user.lastName,
            `${user.firstName || ''} ${user.lastName || ''}`.trim(),
            user.city,
            user.email
          ]
          return searchable.some(includesTerm)
        })
      setUsers(usersData)

      const centersData = centersSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(center => {
          const searchable = [
            center.name,
            center.address,
            center.city,
            ...(center.sports || [])
          ]
          return searchable.some(includesTerm)
        })
      setCenters(centersData)
    } catch (error) {
      console.error('Error searching:', error)
      setMatches([])
      setUsers([])
      setCenters([])
    } finally {
      setLoading(false)
    }
  }

  const filteredMatches = activeTab === 'all' || activeTab === 'matches' ? matches : []
  const filteredUsers = activeTab === 'all' || activeTab === 'users' ? users : []
  const filteredCenters = activeTab === 'all' || activeTab === 'centers' ? centers : []

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Search Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <SearchBar
          onSearch={setSearchTerm}
          placeholder={t('search.searchPlaceholder')}
          initialValue={searchTerm}
        />
      </div>

      {searchTerm && (
        <>
          {/* Tabs */}
          <div className="grid grid-cols-4 gap-1 border-b border-gray-200 dark:border-gray-700 sm:flex sm:gap-2 sm:overflow-x-auto">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-2 py-2 sm:px-6 sm:py-3 text-[11px] sm:text-base font-semibold transition text-center whitespace-normal sm:whitespace-nowrap min-w-0 ${
                activeTab === 'all'
                  ? 'text-sport-primary border-b-2 border-sport-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('search.all')} ({matches.length + users.length + centers.length})
            </button>
            <button
              onClick={() => setActiveTab('matches')}
              className={`px-2 py-2 sm:px-6 sm:py-3 text-[11px] sm:text-base font-semibold transition text-center whitespace-normal sm:whitespace-nowrap min-w-0 ${
                activeTab === 'matches'
                  ? 'text-sport-primary border-b-2 border-sport-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('search.matches')} ({matches.length})
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-2 py-2 sm:px-6 sm:py-3 text-[11px] sm:text-base font-semibold transition text-center whitespace-normal sm:whitespace-nowrap min-w-0 ${
                activeTab === 'users'
                  ? 'text-sport-primary border-b-2 border-sport-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('search.players')} ({users.length})
            </button>
            <button
              onClick={() => setActiveTab('centers')}
              className={`px-2 py-2 sm:px-6 sm:py-3 text-[11px] sm:text-base font-semibold transition text-center whitespace-normal sm:whitespace-nowrap min-w-0 ${
                activeTab === 'centers'
                  ? 'text-sport-primary border-b-2 border-sport-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('search.sportsCenters')} ({centers.length})
            </button>
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-sport-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Matches */}
              {filteredMatches.map((match, index) => (
                <motion.div
                  key={match.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/match/${match.id}`)}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg hover:shadow-xl transition cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-sport-primary/20 flex items-center justify-center">
                      <CalendarIcon className="h-6 w-6 text-sport-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                        {match.title || (match.sport === 'calcio' ? t('common.footballMatch') : t('common.padelMatch'))}
                      </h3>
                      <p className="text-sm text-gray-500 truncate">{match.location || t('common.locationTbd')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-sport-primary">{match.currentPlayers || 0}/{match.maxPlayers || 0}</p>
                      <p className="text-xs text-gray-500">
                        {match.date ? format(new Date(match.date), "PPP p", { locale }) : t('common.dateTbd')}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Users */}
              {filteredUsers.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/profile/${user.id}`)}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg hover:shadow-xl transition cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-sport-primary to-sport-secondary flex items-center justify-center text-white font-bold">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {user.firstName} {user.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{user.city}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-sport-primary">{user.points || 0} {t('common.pointsShort')}</p>
                      <p className="text-xs text-gray-500">{user.matchesPlayed || 0} {t('common.matchesShort')}</p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Sports Centers */}
              {filteredCenters.map((center, index) => (
                <motion.div
                  key={center.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg hover:shadow-xl transition"
                >
                  <div className="flex items-center space-x-4">
                    <div className="h-12 w-12 rounded-full bg-sport-primary/20 flex items-center justify-center">
                      <BuildingOfficeIcon className="h-6 w-6 text-sport-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">{center.name}</h3>
                      <p className="text-sm text-gray-500">{center.address}</p>
                      <CenterPitchSlots center={center} variant="inline" />
                    </div>
                    <div className="shrink-0 self-start text-right">
                      <p className="text-sm font-semibold text-sport-primary">{center.sports?.join(', ')}</p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {filteredMatches.length === 0 && filteredUsers.length === 0 && filteredCenters.length === 0 && (
                <div className="text-center py-12">
                  <MagnifyingGlassIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">{t('search.noResultsFound', { term: searchTerm })}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Search